import React, { useEffect, useState } from "react";
import MenuButtons from "../../components/MenuButtons";
import { useAuth } from "../../services/AuthContext";
import GraphTotalSavings from "../../components/GraphTotalSavings";
import {
  fetchChildBalance,
  fetchInterestRates,
  fetchMonthlyBalanceHistory,
  fetchChildSavingsInfo,
} from "../../services/Api";
import LoadingSpinner from "../../components/LoadingSpinner";

const NUM_MONTHS_TO_FORECAST = 24;
const TOGGLE_PAST_BALANCE = "History";
const TOGGLE_FORECAST = "Future";
const monthAbbreviations = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const InvestmentPage = () => {
  const { childID } = useAuth();
  const [rawBalanceHistory, setRawBalanceHistory] = useState([]);
  const [monthBalanceHistory, setMonthBalanceHistory] = useState([]);
  const [selected, setSelected] = useState(TOGGLE_PAST_BALANCE);
  const [primaryInterestRate, setPrimaryInterestRate] = useState();
  const [primaryMax, setPrimaryMax] = useState();
  const [secondaryInterestRate, setSecondaryInterestRate] = useState();
  const [balance, setBalance] = useState();
  const [forecastedData, setForecastedData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);

  //get the balance history from Backend
  useEffect(() => {
    if (!childID) return;
    setIsSpinnerLoading(true);
    const fetchData = async () => {
      try {
        const minDelay = new Promise((resolve) => setTimeout(resolve, 200));
        const [balanceHistoryRes, interestRes, balanceRes, childSavingsRes] =
          await Promise.all([
            fetchMonthlyBalanceHistory(childID),
            fetchInterestRates(childID),
            fetchChildBalance(childID),
            fetchChildSavingsInfo(childID),
            minDelay,
          ]);
        setRawBalanceHistory(balanceHistoryRes?.monthData);
        setPrimaryInterestRate(interestRes?.interestResults?.InitialRate);
        setSecondaryInterestRate(interestRes?.interestResults?.EndingRate);
        setPrimaryMax(interestRes?.interestResults?.InitialMaximum);
        setBalance(balanceRes?.balance);
        setFirstName(childSavingsRes?.childInfo.firstName);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsSpinnerLoading(false);
      }
    };

    fetchData();
  }, [childID]);

  //format balance history so that the month is put with the year
  useEffect(() => {
    const formatBalanceHistory = () => {
      const eachMonthBalanceArray = [];
      rawBalanceHistory?.forEach(({ Month, Year, EndOfMonthBalance }) => {
        const entry = {
          Month: `${monthAbbreviations[Month - 1]}-${Year}`,
          Balance: Number(EndOfMonthBalance),
        };
        eachMonthBalanceArray.push(entry);
      });

      setMonthBalanceHistory(eachMonthBalanceArray);
    };

    const calculateForecastedValues = () => {
      if (rawBalanceHistory && rawBalanceHistory.length < 1) {
        setForecastedData([]);
        return;
      }

      const monthForecastVal = [];
      var startBalance = balance;
      const startMonth = rawBalanceHistory && rawBalanceHistory[rawBalanceHistory.length - 1]?.Month;
      var startYear = rawBalanceHistory && rawBalanceHistory[rawBalanceHistory.length - 1]?.Year;
      var yearTracker = startMonth;
      for (let i = 0; i < NUM_MONTHS_TO_FORECAST + 1; i++) {
        //calculate startBalance with over the primaryMax
        if (startBalance <= primaryMax) {
          startBalance = startBalance * (primaryInterestRate / 100 + 1);
        } else {
          let amountOverMax = startBalance - primaryMax;
          startBalance = primaryMax * (primaryInterestRate / 100 + 1);
          startBalance += amountOverMax * (secondaryInterestRate / 100 + 1);
        }
        startBalance = parseFloat(startBalance.toFixed(2));

        const entry = {
          Month: `${
            monthAbbreviations[(startMonth + i - 1) % 12]
          }-${startYear}`,
          Balance: startBalance,
        };
        monthForecastVal.push(entry);

        if (i + yearTracker > 11) {
          startYear += 1;
          yearTracker -= 12;
        }
      }
      setForecastedData(monthForecastVal);
    };

    formatBalanceHistory();
    calculateForecastedValues();
  }, [
    balance,
    primaryInterestRate,
    rawBalanceHistory,
    primaryMax,
    secondaryInterestRate,
  ]);

  //changes the data for the graph
  useEffect(() => {
    if (selected === TOGGLE_PAST_BALANCE) {
      setGraphData(monthBalanceHistory);
    } else if (selected === TOGGLE_FORECAST) {
      setGraphData(forecastedData);
    }
  }, [selected, monthBalanceHistory, forecastedData]);

  return (
    <>
      <MenuButtons />
      <LoadingSpinner isLoading={isSpinnerLoading} />
      {/* add a div fade-in and conditional visible classes to get fade in for content working*/}
      <div className={`fade-in ${!isSpinnerLoading ? "visible" : ""}`}>
        <div className="goal-info" style={{ textAlign: "center" }}>
          <p>{`Start Investing. Your monthly interest rate is ${primaryInterestRate ?? 0}%`}</p>
          <div className="flex items-center justify-center mt-4">
            <div className="flex rounded-full p-1">
              {[TOGGLE_PAST_BALANCE, TOGGLE_FORECAST].map((option) => (
                <button
                  key={option}
                  className={`investmentToggleButton ${
                    selected === option ? "selected" : ""
                  }`}
                  onClick={() => setSelected(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <GraphTotalSavings data={graphData} />
        </div>
      </div>
    </>
  );
};

export default InvestmentPage;
